// TeamAvailability.jsx - Fixed version with correct response structure handling
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Grid,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Button,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { axiosInstance } from "../../../utils/axios";
import { useSpace } from "../../../contexts/SpaceContext";
import { formatDateForApi, parseTimeStringToHour } from "./utils";

// Import components
import TeamFilter from "./TeamFilter";
import TeamCalendar from "./TeamCalendar";
import TimeSlots from "./TimeSlots";

// Import GraphQL queries - fixed to not include clientId on TeamMember
const GET_TEAMS = `
  query GetTeams($spaceId: ID) {
    getTeams(spaceId: $spaceId) {
      id
      name
      clientId
      userId
      members {
        id
        userId
        email
        role
        status
        user {
          id
          name
          email
        }
      }
    }
  }
`;

const GET_AVAILABILITIES = `
  query GetAllAvailabilities($spaceId: ID, $includeTeamMembers: Boolean) {
    getAllAvailabilities(spaceId: $spaceId, includeTeamMembers: $includeTeamMembers) {
      id
      name
      timezone
      days
      timeSlots {
        timeStart
        timeEnd
      }
      dayTimeSlots {
        day
        slots {
          timeStart
          timeEnd
        }
      }
      overrides {
        date
        timeSlots {
          timeStart
          timeEnd
        }
        isUnavailable
      }
      clientId
      userId
      spaceId
      createdAt
      updatedAt
    }
  }
`;

const GET_AVAILABLE_TIME_SLOTS = `
  query GetAvailableTimeSlots($userId: ID, $clientId: ID, $date: String!, $spaceId: ID) {
    getAvailableTimeSlots(userId: $userId, clientId: $clientId, date: $date, spaceId: $spaceId) {
      timeStart
      timeEnd
      userId
      clientId
      ownerName
    }
  }
`;

// Add the new GET_MEMBER_BOOKINGS query
const GET_MEMBER_BOOKINGS = `
  query GetMemberBookings($userId: ID, $clientId: ID, $date: String) {
    getMemberBookings(userId: $userId, clientId: $clientId, date: $date) {
      id
      eventTypeId
      date
      timeStart
      timeEnd
      name
      email
      status
      timezone
      clientId
      userId
      eventType {
        id
        title
        availabilityId
        owner {
          id
          name
        }
      }
    }
  }
`;

/**
 * TeamAvailability Component
 *
 * Displays and manages team calendars, availability, and bookings.
 *
 * @param {Object} props - Component props
 * @param {string} props.spaceId - The ID of the space to display availability for
 */
const TeamAvailability = ({ spaceId }) => {
  const theme = useTheme();
  const { currentSpace } = useSpace();
  const isInitialMount = useRef(true);
  const prevSelectedDate = useRef(null);

  // State for teams and members
  const [teams, setTeams] = useState([]);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [visibleMembers, setVisibleMembers] = useState({});
  const [activeTeamId, setActiveTeamId] = useState(null);
  const [activeMemberId, setActiveMemberId] = useState(null);

  // State for data
  const [availabilities, setAvailabilities] = useState([]);

  // State for calendar and time slots
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedDateBookings, setSelectedDateBookings] = useState([]);
  const [activeMemberAvailability, setActiveMemberAvailability] =
    useState(null);

  // UI states
  const [loading, setLoading] = useState(true);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [error, setError] = useState(null);

  // Flag to prevent effect chaining
  const isUpdatingAvailability = useRef(false);

  // Get the effective space ID to use for API calls
  const effectiveSpaceId = useMemo(
    () => spaceId || currentSpace?.id,
    [spaceId, currentSpace]
  );

  /**
   * Helper function to get member name
   */
  const getMemberName = useCallback((member) => {
    if (!member) return "Unknown";

    // Check user object first
    if (member.user?.name) return member.user.name;

    // Check direct name property
    if (member.name) return member.name;

    // Check email and extract username part
    if (member.email) return member.email.split("@")[0];

    return "Unknown";
  }, []);

  /**
   * Fetch time slots and bookings for a specific date and member
   */
  const fetchTimeSlots = useCallback(
    async (
      date,
      teamsData,
      expandedTeamsState,
      visibleMembersState = {},
      currentActiveMemberId = null
    ) => {
      if (!teamsData?.length || isUpdatingAvailability.current) {
        return;
      }

      try {
        isUpdatingAvailability.current = true;
        setLoadingAvailability(true);
        const formattedDate = formatDateForApi(date);

        // Find active member and the team it belongs to
        let activeMember = null;
        let activeTeam = null;
        const activeMembers = [];

        // Use passed parameter instead of closure value to avoid stale data
        const targetMemberId = currentActiveMemberId || activeMemberId;

        // Loop through teams to find active members
        teamsData.forEach((team) => {
          if (expandedTeamsState[team.id]) {
            team.members.forEach((member) => {
              console.log("Member Details:", member);
              const memberId = member.userId || member.id;
              if (
                member.status === "active" &&
                memberId &&
                visibleMembersState[memberId]
              ) {
                // Add display name to member object
                const enhancedMember = {
                  ...member,
                  displayName: getMemberName(member),
                  teamId: team.id,
                  teamClientId: team.clientId, // Store the team's clientId with the member
                };

                activeMembers.push(enhancedMember);
                if (memberId === targetMemberId) {
                  activeMember = enhancedMember;
                  activeTeam = team; // Store the active team
                }
              }
            });
          }
        });

        // If no active member found but we have members with visibility on, use the first one
        if (!activeMember && activeMembers.length > 0) {
          activeMember = activeMembers[0];
          const newActiveMemberId = activeMember.userId || activeMember.id;
          setActiveMemberId(newActiveMemberId);

          // Find the team for this member
          for (const team of teamsData) {
            if (team.id === activeMember.teamId) {
              activeTeam = team;
              break;
            }
          }
        }

        // If no active member, exit
        if (!activeMember) {
          setCalendarEvents([]);
          setSelectedDateBookings([]);
          setActiveMemberAvailability(null);
          setLoadingAvailability(false);
          isUpdatingAvailability.current = false;
          return;
        }

        // Get member ID for filtering
        const memberId = activeMember.userId || activeMember.id;

        // Determine if this is a user or client member
        const isUserMember = !!activeMember.userId;

        console.log("Active Member:", JSON.stringify(activeMember, null, 2));
        console.log("Active Team:", JSON.stringify(activeTeam, null, 2));

        // Get the correct clientId - use the team's clientId for client members
        let clientIdForQuery = null;

        if (!isUserMember) {
          // For client members, use the team's clientId
          clientIdForQuery = activeTeam ? activeTeam.clientId : null;
          console.log(`Using clientId for queries: ${clientIdForQuery}`);
        }

        // Prepare variables for fetching bookings
        let bookingVariables = {
          date: formattedDate,
        };

        if (isUserMember) {
          bookingVariables.userId = activeMember.userId;
        } else if (clientIdForQuery) {
          bookingVariables.clientId = clientIdForQuery;
        }

        console.log(
          "Booking Query Variables:",
          JSON.stringify(bookingVariables, null, 2)
        );

        // Fetch bookings for this member
        const bookingsResponse = await axiosInstance.post("/graphql", {
          query: GET_MEMBER_BOOKINGS,
          variables: bookingVariables,
        });

        console.log(
          "Bookings Response:",
          JSON.stringify(bookingsResponse.data, null, 2)
        );

        // Process bookings for display - access data directly from response.data
        const memberBookings = bookingsResponse.data?.getMemberBookings || [];

        console.log("Member Bookings:", memberBookings.length);

        const calendarEvents = [];
        const processedBookings = [];

        memberBookings.forEach((booking) => {
          // Use display name
          const ownerName = activeMember.displayName || "Unknown";
          const customerName = booking.name || "Unnamed Customer";

          // Create calendar event for confirmed bookings
          if (booking.status === "confirmed") {
            const startDate = new Date(`${booking.date}T${booking.timeStart}`);
            const endDate = new Date(`${booking.date}T${booking.timeEnd}`);

            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
              calendarEvents.push({
                id: booking.id,
                title: `${ownerName}: ${customerName}`,
                start: startDate,
                end: endDate,
                resource: {
                  isBooked: true,
                  booking: booking,
                  memberId: memberId,
                  memberName: ownerName,
                },
              });
            }
          }

          processedBookings.push({
            ownerId: memberId,
            ownerName,
            timeStart: booking.timeStart,
            timeEnd: booking.timeEnd,
            customerName,
            customerEmail: booking.email,
            eventTitle: booking.eventType?.title || "Appointment",
            isBooked: true,
          });
        });

        console.log("Calendar Events Created:", calendarEvents.length);
        console.log("Processed Bookings:", processedBookings.length);

        // Update state with bookings
        setCalendarEvents(calendarEvents);
        setSelectedDateBookings(processedBookings);

        // Find the active member's availability schedule
        // This will be used to display the simple availability preview
        let memberAvailability = null;

        // Debug: Log all availabilities and what we're looking for
        console.log(
          "Looking for availability - activeMember.userId:",
          activeMember.userId
        );
        console.log(
          "Available availabilities (full):",
          JSON.stringify(
            availabilities.map((a) => ({
              id: a.id,
              userId: a.userId,
              clientId: a.clientId,
              name: a.name,
              spaceId: a.spaceId,
            })),
            null,
            2
          )
        );

        if (isUserMember && activeMember.userId) {
          // Find availability by userId - compare as strings
          memberAvailability = availabilities.find(
            (a) => String(a.userId) === String(activeMember.userId)
          );
        } else if (clientIdForQuery) {
          // Find availability by clientId (and no userId - client's own availability)
          memberAvailability = availabilities.find(
            (a) => String(a.clientId) === String(clientIdForQuery) && !a.userId
          );
        }
        console.log(
          "Member Availability Found:",
          memberAvailability?.name || "None"
        );
        setActiveMemberAvailability(memberAvailability);

        // Prepare variables for fetching available time slots
        const availabilityVariables = {
          userId: isUserMember ? activeMember.userId : null,
          clientId: !isUserMember ? clientIdForQuery : null,
          date: formattedDate,
          spaceId: effectiveSpaceId,
        };

        console.log(
          "Availability Query Variables:",
          JSON.stringify(availabilityVariables, null, 2)
        );

        // Fetch available time slots for the active member
        try {
          const response = await axiosInstance.post("/graphql", {
            query: GET_AVAILABLE_TIME_SLOTS,
            variables: availabilityVariables,
          });

          console.log(
            "Time Slots Response:",
            JSON.stringify(response.data, null, 2)
          );

          // Access getAvailableTimeSlots directly from response.data
          if (response.data && response.data.getAvailableTimeSlots) {
            const slots = response.data.getAvailableTimeSlots;
            console.log("Available Slots:", slots ? slots.length : 0);

            const availableSlots = [];

            slots.forEach((slot) => {
              // Verify slot is for this member - compare as strings
              let isForThisMember = false;

              if (isUserMember) {
                isForThisMember =
                  String(slot.userId) === String(activeMember.userId);
              } else {
                isForThisMember =
                  String(slot.clientId) === String(clientIdForQuery);
              }

              if (!isForThisMember) {
                return;
              }

              // Check if slot overlaps with any booking
              const isBooked = memberBookings.some((booking) => {
                const slotStart = parseTimeStringToHour(slot.timeStart);
                const slotEnd = parseTimeStringToHour(slot.timeEnd);
                const bookingStart = parseTimeStringToHour(booking.timeStart);
                const bookingEnd = parseTimeStringToHour(booking.timeEnd);

                return slotStart < bookingEnd && slotEnd > bookingStart;
              });

              if (!isBooked) {
                availableSlots.push({
                  ownerId: slot.userId || slot.clientId,
                  ownerName:
                    slot.ownerName || activeMember.displayName || "Unknown",
                  timeStart: slot.timeStart,
                  timeEnd: slot.timeEnd,
                  isBooked: false,
                });
              }
            });

            console.log("Final Available Slots:", availableSlots.length);

            // Sort available slots by time
            const sortedAvailability = availableSlots.sort((a, b) => {
              return (
                parseTimeStringToHour(a.timeStart) -
                parseTimeStringToHour(b.timeStart)
              );
            });

            console.log("Sorted Availability:", sortedAvailability.length);
          }
        } catch (error) {
          console.error("Error fetching available time slots:", error);
        }
      } catch (error) {
        console.error("Error in fetchTimeSlots:", error);
        setCalendarEvents([]);
        setSelectedDateBookings([]);
        setActiveMemberAvailability(null);
      } finally {
        setLoadingAvailability(false);
        isUpdatingAvailability.current = false;
      }
    },
    [effectiveSpaceId, activeMemberId, getMemberName]
  );

  /**
   * Initial data loading - fetch teams and availabilities
   */
  useEffect(() => {
    const fetchData = async () => {
      if (!effectiveSpaceId) {
        setError("No space ID available");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch teams and availabilities in parallel
        const [teamsResponse, availabilitiesResponse] = await Promise.all([
          // Teams
          axiosInstance.post("/graphql", {
            query: GET_TEAMS,
            variables: { spaceId: effectiveSpaceId },
          }),
          // Availabilities - pass includeTeamMembers: true to get team members' availabilities
          axiosInstance.post("/graphql", {
            query: GET_AVAILABILITIES,
            variables: { spaceId: effectiveSpaceId, includeTeamMembers: true },
          }),
        ]);

        // Check for errors
        if (teamsResponse.data?.errors) {
          throw new Error(
            teamsResponse.data.errors[0]?.message || "Error fetching teams"
          );
        }
        if (availabilitiesResponse.data?.errors) {
          throw new Error(
            availabilitiesResponse.data.errors[0]?.message ||
              "Error fetching availabilities"
          );
        }

        // Extract data directly from response.data
        const fetchedTeams = teamsResponse.data?.getTeams || [];
        const fetchedAvailabilities =
          availabilitiesResponse.data?.getAllAvailabilities || [];

        console.log("Fetched Teams:", fetchedTeams.length);
        console.log("Fetched Availabilities:", fetchedAvailabilities.length);

        // Set active team if we have teams
        if (fetchedTeams.length > 0 && !activeTeamId) {
          setActiveTeamId(fetchedTeams[0].id);
        }

        // Initialize expanded state for teams (only first team expanded)
        const initialExpandedState = {};
        fetchedTeams.forEach((team, index) => {
          initialExpandedState[team.id] = index === 0;
        });

        // Update state
        setTeams(fetchedTeams);
        setAvailabilities(fetchedAvailabilities);

        // Only set expanded teams if not already set
        setExpandedTeams((prev) => {
          if (Object.keys(prev).length === 0) {
            return initialExpandedState;
          }
          return prev;
        });

        // Initial data processing for the current date
        if (isInitialMount.current) {
          isInitialMount.current = false;
          await fetchTimeSlots(
            selectedDate,
            fetchedTeams,
            initialExpandedState,
            visibleMembers,
            activeMemberId
          );
        }
      } catch (error) {
        console.error("Error in initial data fetch:", error);
        setError(error.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    effectiveSpaceId,
    activeTeamId,
    selectedDate,
    fetchTimeSlots,
    visibleMembers,
  ]);

  /**
   * Fetch time slots when date changes or active member changes
   * This effect is explicitly separate to have better control
   */
  useEffect(() => {
    // Only run this if we have loaded teams and we're not in initial loading state
    if (teams.length > 0 && !loading && !isInitialMount.current) {
      // Compare current and previous date to avoid needless re-fetching
      const currentDateStr = selectedDate.toISOString().split("T")[0];
      const prevDateStr = prevSelectedDate.current
        ? prevSelectedDate.current.toISOString().split("T")[0]
        : null;

      // Update the previous date reference
      prevSelectedDate.current = selectedDate;

      // Only fetch if the date has actually changed (to avoid unnecessary flickering)
      if (currentDateStr !== prevDateStr) {
        // Small delay to prevent UI jank
        const timeoutId = setTimeout(() => {
          fetchTimeSlots(
            selectedDate,
            teams,
            expandedTeams,
            visibleMembers,
            activeMemberId
          );
        }, 10);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    selectedDate,
    teams,
    loading,
    fetchTimeSlots,
    expandedTeams,
    visibleMembers,
  ]);

  /**
   * Handle active member changes separately to prevent unnecessary reloads
   * Pass activeMemberId directly to avoid stale closure issues
   */
  useEffect(() => {
    if (
      teams.length > 0 &&
      !loading &&
      !isInitialMount.current &&
      activeMemberId
    ) {
      // Pass activeMemberId directly to avoid stale closure issues
      fetchTimeSlots(
        selectedDate,
        teams,
        expandedTeams,
        visibleMembers,
        activeMemberId
      );
    }
  }, [
    activeMemberId,
    teams,
    loading,
    fetchTimeSlots,
    selectedDate,
    expandedTeams,
    visibleMembers,
  ]);

  /**
   * Handler for team toggle
   */
  const handleTeamToggle = useCallback((teamId) => {
    // Update expanded teams - only one team can be expanded at a time
    setExpandedTeams((prev) => {
      const newState = { ...prev };

      // If expanding a team that wasn't expanded, collapse others
      if (!prev[teamId]) {
        Object.keys(newState).forEach((key) => {
          newState[key] = key === teamId;
        });
      } else {
        // Just toggle the current team
        newState[teamId] = !prev[teamId];
      }

      return newState;
    });

    // Set active team ID
    setActiveTeamId(teamId);
  }, []);

  /**
   * Handler for member toggle
   */
  const handleMemberToggle = useCallback(
    (memberId, checked) => {
      // Prevent repeated state updates if the state is already correct
      const isAlreadyActive = memberId === activeMemberId;
      const isAlreadyVisible = !!visibleMembers[memberId];

      if (
        (checked && isAlreadyActive && isAlreadyVisible) ||
        (!checked && !isAlreadyActive && !isAlreadyVisible)
      ) {
        return; // No state change needed
      }

      // When turning a member on, turn all others off
      if (checked) {
        // Update both states in one batch to prevent multiple re-renders
        setActiveMemberId(memberId);
        setVisibleMembers((prev) => {
          // Only create a new object if we need to change something
          if (prev[memberId] && Object.keys(prev).length === 1) {
            return prev; // Already correct state
          }

          const newState = {};
          // Set only this member to visible
          newState[memberId] = true;
          return newState;
        });
      } else {
        // If turning off the active member, clear active member
        if (memberId === activeMemberId) {
          setActiveMemberId(null);
        }

        // Update the visibility state
        setVisibleMembers((prev) => {
          // Only create a new object if we need to change something
          if (!prev[memberId]) {
            return prev; // Already correct state
          }

          const newState = { ...prev };
          delete newState[memberId]; // Remove the member instead of setting to false
          return newState;
        });
      }
    },
    [activeMemberId, visibleMembers]
  );

  /**
   * Handler for date selection - with debounce to prevent multiple updates
   */
  const handleSelectDate = useCallback(
    (date) => {
      // Prevent multiple re-renders when selecting the same date
      if (
        !selectedDate ||
        selectedDate.getDate() !== date.getDate() ||
        selectedDate.getMonth() !== date.getMonth() ||
        selectedDate.getFullYear() !== date.getFullYear()
      ) {
        // Use a stable date object to avoid reference issues
        const newDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        );
        setSelectedDate(newDate);
      }
    },
    [selectedDate]
  );

  // Loading state
  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={6}
      >
        <CircularProgress size={40} thickness={4} />
        <Typography variant="body1" color="textSecondary" mt={2}>
          Loading team availability...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        severity="error"
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Typography variant="subtitle1" gutterBottom>
          {error}
        </Typography>
        <Button
          color="error"
          variant="outlined"
          size="small"
          onClick={() => window.location.reload()}
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      </Alert>
    );
  }

  // No teams state
  if (teams.length === 0) {
    return (
      <Alert
        severity="info"
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Typography variant="subtitle1" gutterBottom>
          No teams found
        </Typography>
        <Typography variant="body2">
          Please create a team first to view team availability.
        </Typography>
      </Alert>
    );
  }

  // Main render
  return (
    <Grid container spacing={3}>
      {/* Team Selection Panel - Left Side */}
      <Grid item xs={12} md={3}>
        <TeamFilter
          teams={teams}
          expandedTeams={expandedTeams}
          visibleMembers={visibleMembers}
          activeTeamId={activeTeamId}
          handleTeamToggle={handleTeamToggle}
          handleMemberToggle={handleMemberToggle}
          theme={theme}
        />
      </Grid>

      {/* Calendar and Time Slots - Right Side */}
      <Grid item xs={12} md={9}>
        <TeamCalendar
          calendarEvents={calendarEvents}
          selectedDate={selectedDate}
          handleSelectDate={handleSelectDate}
          theme={theme}
        />

        <TimeSlots
          selectedDate={selectedDate}
          selectedDateBookings={selectedDateBookings}
          availabilityData={activeMemberAvailability}
          loadingAvailability={loadingAvailability}
          activeMemberId={activeMemberId}
          theme={theme}
        />
      </Grid>
    </Grid>
  );
};

/**
 * Wrapper component to provide LocalizationProvider context
 * for date/time functionality
 */
const TeamAvailabilityWrapper = (props) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TeamAvailability {...props} />
    </LocalizationProvider>
  );
};

export default TeamAvailabilityWrapper;
